import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, Trophy, BarChart3, Shield, Activity, TrendingUp, UserCheck, AlertTriangle, RefreshCw, CheckCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { runProfileUpdate } from '../../utils/updateExistingProfiles';

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [isUpdatingProfiles, setIsUpdatingProfiles] = useState(false);
  const [updateResult, setUpdateResult] = useState<{ updated: number; total: number } | null>(null);

  const stats = [
    { label: 'Total Users', value: '2,847', icon: Users, color: 'text-blue-600', change: '+12%' },
    { label: 'Active Companies', value: '156', icon: Shield, color: 'text-green-600', change: '+8%' },
    { label: 'Tests Completed', value: '8,924', icon: Activity, color: 'text-red-600', change: '+24%' },
    { label: 'Badges Issued', value: '1,234', icon: Trophy, color: 'text-purple-600', change: '+15%' }
  ];

  const recentActivity = [
    {
      id: 1,
      type: 'user_registered',
      details: 'New HR user from TechCorp Inc.',
      time: '5 minutes ago',
      status: 'success'
    },
    {
      id: 2,
      type: 'badge_issued',
      details: 'Gold badge issued to John Doe',
      time: '1 hour ago',
      status: 'success'
    },
    {
      id: 3,
      type: 'system_alert',
      details: 'High server load detected',
      time: '2 hours ago',
      status: 'warning'
    },
    {
      id: 4,
      type: 'company_verified',
      details: 'StartupXYZ verification completed',
      time: '4 hours ago',
      status: 'success'
    }
  ];

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'user_registered':
        return <UserCheck className="w-5 h-5 text-green-600" />;
      case 'badge_issued':
        return <Trophy className="w-5 h-5 text-purple-600" />;
      case 'system_alert':
        return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
      case 'company_verified':
        return <Shield className="w-5 h-5 text-blue-600" />;
      default:
        return <Activity className="w-5 h-5 text-gray-600" />;
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'success':
        return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">Success</span>;
      case 'warning':
        return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-full">Warning</span>;
      case 'error':
        return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs rounded-full">Error</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-full">Info</span>;
    }
  };

  const handleUpdateProfiles = async () => {
    setIsUpdatingProfiles(true);
    setUpdateResult(null);
    
    try {
      const result = await runProfileUpdate();
      setUpdateResult(result);
    } catch (error) {
      alert('Failed to update profiles. Please check the console for details.');
    } finally {
      setIsUpdatingProfiles(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="glass-card-gradient relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-red-800/10 via-red-600/5 to-red-800/10"></div>
        <div className="relative flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-600 mt-2">Welcome back, {user?.name}! Monitor and manage the platform</p>
            <div className="mt-4 flex items-center space-x-2">
              <div className="p-2 bg-red-100 rounded-lg backdrop-blur-sm">
                <Shield className="w-5 h-5 text-red-600" />
              </div>
              <span className="text-gray-700 font-medium">System Administrator</span>
            </div>
          </div>
          <div className="hidden md:block">
            <img
              src="https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=300"
              alt="Admin Dashboard"
              className="w-32 h-32 rounded-glass object-cover shadow-glass"
            />
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="grid md:grid-cols-3 gap-6">
        <Link
          to="/admin/users"
          className="glass-card group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-glass flex items-center justify-center shadow-glow-blue">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">Manage Users</h3>
              <p className="text-sm text-gray-600">View and manage all users</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/badges"
          className="glass-card group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-glass flex items-center justify-center shadow-glow-purple">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">Badge Management</h3>
              <p className="text-sm text-gray-600">Issue and manage badges</p>
            </div>
          </div>
        </Link>

        <Link
          to="/admin/analytics"
          className="glass-card group"
        >
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-glass flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 group-hover:text-green-600 transition-colors">Analytics</h3>
              <p className="text-sm text-gray-600">Platform insights</p>
            </div>
          </div>
        </Link>
      </div>

      {/* Stats Overview */}
      <div className="grid md:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="stat-card">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 font-medium">{stat.label}</p>
                  <p className="text-4xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <div className="flex items-center mt-3">
                    <TrendingUp className="w-4 h-4 text-green-600 mr-1" />
                    <span className="text-sm text-green-600 font-semibold">{stat.change}</span>
                  </div>
                </div>
                <div className={`p-3 rounded-glass bg-gradient-to-br ${
                  index === 0 ? 'from-blue-500 to-blue-600' :
                  index === 1 ? 'from-green-500 to-green-600' :
                  index === 2 ? 'from-red-500 to-red-600' :
                  'from-purple-500 to-purple-600'
                }`}>
                  <Icon className="w-8 h-8 text-white" />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent Activity & System Health */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="glass-card">
          <div className="pb-4 border-b border-gray-200/50">
            <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
          </div>
          <div className="pt-6">
            <div className="space-y-3">
              {recentActivity.map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-4 surface-subtle rounded-glass hover:bg-white/60 transition-all">
                  <div className="flex items-center space-x-4">
                    {getActivityIcon(activity.type)}
                    <div>
                      <p className="font-medium text-gray-900">{activity.details}</p>
                      <p className="text-sm text-gray-600">{activity.time}</p>
                    </div>
                  </div>
                  {getStatusBadge(activity.status)}
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="glass-card">
          <div className="pb-4 border-b border-gray-200/50">
            <h2 className="text-xl font-semibold text-gray-900">System Health</h2>
          </div>
          <div className="pt-6 space-y-6">
            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700 font-medium">Server Performance</span>
                <span className="text-green-600 font-semibold">98.5%</span>
              </div>
              <div className="w-full bg-gray-200/50 rounded-full h-2.5 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-green-500 to-green-600 h-2.5 rounded-full shadow-sm" style={{ width: '98.5%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700 font-medium">Database Health</span>
                <span className="text-green-600 font-semibold">99.2%</span>
              </div>
              <div className="w-full bg-gray-200/50 rounded-full h-2.5 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-green-500 to-green-600 h-2.5 rounded-full shadow-sm" style={{ width: '99.2%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700 font-medium">API Response Time</span>
                <span className="text-yellow-600 font-semibold">245ms</span>
              </div>
              <div className="w-full bg-gray-200/50 rounded-full h-2.5 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 h-2.5 rounded-full shadow-sm" style={{ width: '75%' }}></div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2">
                <span className="text-gray-700 font-medium">Storage Usage</span>
                <span className="text-blue-600 font-semibold">67%</span>
              </div>
              <div className="w-full bg-gray-200/50 rounded-full h-2.5 backdrop-blur-sm">
                <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2.5 rounded-full shadow-sm" style={{ width: '67%' }}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Admin Utilities */}
      <div className="glass-card">
        <div className="pb-4 border-b border-gray-200/50">
          <h2 className="text-xl font-semibold text-gray-900">Admin Utilities</h2>
          <p className="text-gray-600 text-sm mt-1">Tools for managing platform data and user verification</p>
        </div>
        <div className="pt-6">
          <div className="surface-subtle border border-blue-200/50 rounded-glass p-5">
            <h3 className="font-semibold text-blue-900 mb-2 flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              Skills Verification Update
            </h3>
            <p className="text-gray-700 text-sm mb-4">
              Update existing jobseeker profiles to show verification status based on completed tests with scores ≥60%.
              This will add green checkmarks to profiles of users who have already passed skills validation tests.
            </p>

            <div className="flex items-center space-x-4">
              <button
                onClick={handleUpdateProfiles}
                disabled={isUpdatingProfiles}
                className="btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUpdatingProfiles ? (
                  <>
                    <RefreshCw className="w-4 h-4 animate-spin" />
                    Updating Profiles...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Update Verification Status
                  </>
                )}
              </button>

              {updateResult && (
                <div className="flex items-center space-x-2 px-4 py-2 bg-green-50 rounded-glass border border-green-200">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">
                    Updated {updateResult.updated} of {updateResult.total} profiles
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Platform Overview */}
      <div className="glass-card">
        <div className="pb-4 border-b border-gray-200/50">
          <h2 className="text-xl font-semibold text-gray-900">Platform Overview</h2>
        </div>
        <div className="pt-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="text-center p-6 surface-subtle rounded-glass">
              <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-blue-500 bg-clip-text text-transparent mb-2">2,847</div>
              <div className="text-gray-700 font-semibold">Total Users</div>
              <div className="text-sm text-gray-600 mt-2">
                Job Seekers: 2,234 • HR: 456 • Admins: 12
              </div>
            </div>
            <div className="text-center p-6 surface-subtle rounded-glass">
              <div className="text-4xl font-bold bg-gradient-to-r from-green-600 to-green-500 bg-clip-text text-transparent mb-2">8,924</div>
              <div className="text-gray-700 font-semibold">Tests Completed</div>
              <div className="text-sm text-gray-600 mt-2">
                This month: 1,456 • Success rate: 78%
              </div>
            </div>
            <div className="text-center p-6 surface-subtle rounded-glass">
              <div className="text-4xl font-bold bg-gradient-to-r from-purple-600 to-purple-500 bg-clip-text text-transparent mb-2">1,234</div>
              <div className="text-gray-700 font-semibold">Badges Issued</div>
              <div className="text-sm text-gray-600 mt-2">
                Silver: 892 • Gold: 342
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;